import React, { useState } from "react";
import "./login.css";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import login_img from "../../assets/image/user_login.jpeg";
import { api } from "../../api/api";
import { useAuthContext } from "../../AuthContextAPI";
import { jwtDecode } from "jwt-decode";
import CountryCode from "../../Components/common/CountryCode/CountryCode";

// Pull the most useful message out of the backend's error shapes
const getErrorMessage = (res) =>
  res?.errors?.[0]?.msg ||
  res?.errors?.[0]?.message ||
  res?.response ||
  res?.message ||
  res?.error ||
  "Login failed. Please try again.";

const Login = () => {
  const { doLogin, setProfileData } = useAuthContext();
  const [showPassword, setShowPassword] = useState(false);
  const [loginNumber, setLoginNumber] = useState(true); // true = phone login (default), false = email login
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null); // { title, message, kind }

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      countryPhone: { countryCode: "+91", phone: "" },
    },
    mode: "onSubmit",
  });

  const registerOptions = {
    email: {
      required: "Email is required",
      pattern: {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "Enter a valid email address",
      },
    },
    password: { required: "Password is required" },
  };

  const switchMode = (toPhone) => {
    if (toPhone === loginNumber) return;
    setFormError(null);
    clearErrors();
    setLoginNumber(toPhone);
  };

  // Turn backend/network errors into a friendly title + message
  const toFriendlyError = (msg) => {
    if (/invalid credentials/i.test(msg)) {
      return {
        kind: "credentials",
        title: "Incorrect login details",
        message: `The ${loginNumber ? "phone number" : "email"} or password you entered doesn't match our records. Please check and try again.`,
      };
    }
    if (/login failed|something went wrong|no response|network/i.test(msg)) {
      return {
        kind: "network",
        title: "We couldn't log you in",
        message: "Please check your internet connection and try again in a moment.",
      };
    }
    return { kind: "other", title: "Login failed", message: msg };
  };

  const onSubmit = async (formData) => {
    setFormError(null);
    try {
      setSubmitting(true);

      let payload;
      if (loginNumber) {
        const code = (formData?.countryPhone?.countryCode || "").trim();
        const digits = String(formData?.countryPhone?.phone || "").replace(/\D/g, "");
        const dial = code.startsWith("+") ? code : `+${code}`;

        // Phones are stored in E.164 form (e.g. +919876543210), same as Register
        payload = { phone: `${dial}${digits}`, password: formData.password };
      } else {
        payload = { email: formData.email.trim(), password: formData.password };
      }

      // hideMsg=true: errors are shown inline on the form, not as a toast
      const res = await api(
        "api/v1/student/login",
        payload,
        "postWithoutToken",
        "",
        "",
        "",
        true,
        false,
        true
      );
      if (!res) throw new Error("No response from server");

      // Unverified account → OTP page (phone is mandatory, so prefer it; email is optional)
      if (res?.data?.isVerified === false) {
        const phone = res?.data?.phone || payload.phone;
        const email = res?.data?.email || payload.email;
        const q = phone
          ? `phone=${encodeURIComponent(phone)}`
          : `email=${encodeURIComponent(email)}`;
        navigate(`/otp?${q}&page=login`);
        return;
      }

      const token = res?.data?.token;
      const success = res?.status === true || res?.success === true;
      if (!success || !token) throw new Error(getErrorMessage(res));

      const user = jwtDecode(token);
      doLogin({ token, ...user });
      await getProfile(token);
      navigate("/");
    } catch (err) {
      setFormError(toFriendlyError(err?.message || "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const getProfile = async (token) => {
    try {
      const res = await api("api/v1/auth/getProfileData", {}, "get", token);
      if (res?.status === true || res?.success === true) {
        localStorage.setItem("profile", JSON.stringify(res?.data));
        setProfileData(res?.data);
      }
    } catch (e) {
      // profile fetch is best-effort
    }
  };

  return (
    <div className="register-area login-area overflow-hidden">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <h3 className="text-center mb-4">
              Welcome To Login
              <span>Please enter your details to continue.</span>
            </h3>
            <div className="img_sec img_showcase">
              <img src={login_img} alt="Login Visual" />
              <span className="img_chip chip_top" aria-hidden="true">
                <i className="fas fa-graduation-cap" /> Learn anytime
              </span>
              <span className="img_chip chip_bottom" aria-hidden="true">
                <i className="fas fa-shield-alt" /> Secure login
              </span>
            </div>

            <div className="contact-form">
              <form
                className={`mt-4 ${formError?.kind === "credentials" ? "credentials-error" : ""}`}
                onSubmit={handleSubmit(onSubmit)}
                onChange={() => formError && setFormError(null)}
                noValidate
              >
                <div className="row">
                  <div className="col-md-10 mx-auto">
                    {/* Email / Phone switch */}
                    <div className="login-switch" role="tablist" aria-label="Login method">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={loginNumber}
                        className={loginNumber ? "active" : ""}
                        onClick={() => switchMode(true)}
                      >
                        <i className="fas fa-mobile-alt" /> Phone Number
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={!loginNumber}
                        className={!loginNumber ? "active" : ""}
                        onClick={() => switchMode(false)}
                      >
                        <i className="fas fa-envelope" /> Email
                      </button>
                    </div>

                    {loginNumber ? (
                      <div className="mb-3 px-2">
                        <Controller
                          name="countryPhone"
                          control={control}
                          rules={{
                            validate: (value) => {
                              const code = (value?.countryCode || "").trim();
                              const digits = String(value?.phone || "").replace(/\D/g, "");
                              if (!code) return "Country code is required";
                              if (!digits) return "Phone number is required";
                              if (digits.length !== 10) return "Enter a valid 10-digit phone number";
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <CountryCode
                              label=""
                              placeholder="*Phone Number"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                        {errors.countryPhone && (
                          <p className="text-danger small mb-0">{errors.countryPhone.message}</p>
                        )}
                      </div>
                    ) : (
                      <div className="mb-3 px-2">
                        <input
                          type="email"
                          className="form-control"
                          placeholder="*Email"
                          autoComplete="email"
                          {...register("email", registerOptions.email)}
                        />
                        {errors.email && (
                          <p className="text-danger small mb-0">{errors.email.message}</p>
                        )}
                      </div>
                    )}

                    {/* PASSWORD */}
                    <div className="mb-3 px-2">
                      <div className="password">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          placeholder="*Password"
                          autoComplete="current-password"
                          {...register("password", registerOptions.password)}
                        />
                        <span className="icon">
                          <i
                            className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                            onClick={() => setShowPassword((s) => !s)}
                            onKeyDown={(e) => e.key === "Enter" && setShowPassword((s) => !s)}
                            role="button"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            tabIndex={0}
                          ></i>
                        </span>
                      </div>
                      {errors.password && (
                        <p className="text-danger small mb-0">{errors.password.message}</p>
                      )}
                      <div className="text-end mt-2">
                        <Link to="/forgot-password" className="forgot-link">
                          Forgot Password?
                        </Link>
                      </div>
                    </div>

                    {formError && (
                      <div className="login-error-card mx-2" role="alert">
                        <span className="lec-icon">
                          <i className={`fas ${formError.kind === "network" ? "fa-wifi" : "fa-exclamation"}`} />
                        </span>
                        <div className="lec-body">
                          <strong>{formError.title}</strong>
                          <p>{formError.message}</p>
                          {formError.kind === "credentials" && (
                            <p className="lec-hint">
                              Forgot your password?
                              <Link to="/forgot-password" className="ms-1">
                                Reset it here
                              </Link>
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="lec-close"
                          aria-label="Dismiss"
                          onClick={() => setFormError(null)}
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    )}

                    <div className="px-2">
                      <button
                        className="btn_secondary btn_md w-100 mt-3 text-center"
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting && (
                          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                        )}
                        {submitting ? "Logging in..." : "Login"}
                      </button>
                    </div>

                    <p className="already">
                      Don't have an account?
                      <Link to="/register" className="ms-2">
                        Sign Up
                      </Link>
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
